import { IExerciseRecords } from 'hooks/useExerciseRecords'
import { FC, useMemo, useState } from 'react'
import { BarChart } from 'components/BarChart'
import { DonutChart } from 'components/DonutChart'
import { CHART_COLORS } from 'components/charts/chartTheme'
import { ChartBox, Section, SectionHeading } from 'components/ui/Section'
import { Segmented } from 'components/ui/Segmented'
import { ChartSkeleton, Skeleton } from 'components/ui/States'
import { formatFixed, formatInt, formatMinSec, groupByExercise, groupByMode } from 'utils/stats'
import { getExerciseName, getModeName } from 'variables/excersise'

type IMetric = 'sessions' | 'reps' | 'minutes'

const METRICS: { value: IMetric, label: string, series: string, unit: string }[] = [
    { value: 'sessions', label: '次數', series: '次數', unit: '次' },
    { value: 'reps', label: '完成次數', series: '完成次數', unit: '下' },
    { value: 'minutes', label: '時間', series: '運動時間', unit: '分鐘' },
]

interface IExerciseSection {
    data: IExerciseRecords[]
    range: string
    loading: boolean
}

/** Section G: exercises and modes. */
export const ExerciseSection: FC<IExerciseSection> = ({ data, range, loading }) => {
    const [metric, setMetric] = useState<IMetric>('sessions')
    const exercises = useMemo(() => groupByExercise(data), [data])
    const modes = useMemo(() => groupByMode(data), [data])
    const metricInfo = METRICS.find((m) => m.value === metric) ?? METRICS[0]

    const ranked = useMemo(() => {
        const value = (e: typeof exercises[number]) =>
            metric === 'sessions' ? e.sessions : metric === 'reps' ? e.reps : Math.round(e.seconds / 60)
        return exercises.map((e) => ({ name: getExerciseName(e.exercise), value: value(e) })).sort((a, b) => b.value - a.value)
    }, [exercises, metric])

    const barHeight = Math.max(240, ranked.length * 40 + 40)

    return <div className='space-y-4 lg:space-y-6'>
        <SectionHeading title='運動項目分析' description='哪些運動及模式最受歡迎' range={range} />
        <div className='grid grid-cols-1 gap-4 lg:grid-cols-5 lg:gap-6'>
            <Section
                className='lg:col-span-3'
                title='運動項目排行'
                subtitle={`${metricInfo.label} · ${metricInfo.unit} · ${range}`}
                actions={<Segmented<IMetric>
                    ariaLabel='排行指標'
                    options={METRICS.map(({ value, label }) => ({ value, label }))}
                    value={metric}
                    onChange={setMetric} />}>
                {loading
                    ? <ChartSkeleton />
                    : <div className='w-full min-w-0' style={{ height: barHeight }}>
                        <BarChart
                            horizontal
                            categories={ranked.map((r) => r.name)}
                            series={[{ name: metricInfo.series, data: ranked.map((r) => r.value) }]}
                            unit={metricInfo.unit}
                            xTitle={metricInfo.unit} />
                    </div>}
            </Section>

            <Section className='lg:col-span-2' title='模式分佈' subtitle={`次數及佔比 · ${range}`}>
                {loading
                    ? <ChartSkeleton />
                    : <div className='flex flex-col items-center gap-4 sm:flex-row lg:flex-col'>
                        <div className='w-full max-w-[260px] shrink-0'>
                            <ChartBox>
                                <DonutChart
                                    labels={modes.map((m) => getModeName(m.mode))}
                                    values={modes.map((m) => m.count)}
                                    unit='次'
                                    totalLabel='總次數' />
                            </ChartBox>
                        </div>
                        <ul className='w-full space-y-2'>
                            {modes.map((m, i) => <li key={m.mode} className='flex items-center justify-between gap-3 rounded-lg bg-canvas px-3 py-2 text-sm'>
                                <span className='flex min-w-0 items-center gap-2'>
                                    <span aria-hidden className='h-2.5 w-2.5 shrink-0 rounded-full' style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                                    <span className='truncate font-medium text-ink'>{getModeName(m.mode)}</span>
                                </span>
                                <span className='shrink-0 tabular-nums text-ink-muted'>
                                    {formatInt(m.count)} 次 · <span className='font-semibold text-ink'>{formatFixed(m.percent, 1)}%</span>
                                </span>
                            </li>)}
                        </ul>
                    </div>}
            </Section>
        </div>

        <Section title='項目明細表' subtitle={`各運動項目的次數、完成次數及時間 · ${range}`} bodyClassName='!p-0'>
            <div className='overflow-x-auto'>
                <table className='w-full min-w-[640px] text-left text-sm'>
                    <thead className='bg-canvas-deep text-xs font-semibold text-ink-muted'>
                        <tr>
                            <th scope='col' className='whitespace-nowrap px-4 py-3 sm:px-6'>項目</th>
                            <th scope='col' className='whitespace-nowrap px-4 py-3 text-right'>次數</th>
                            <th scope='col' className='whitespace-nowrap px-4 py-3 text-right'>總完成次數</th>
                            <th scope='col' className='whitespace-nowrap px-4 py-3 text-right'>總時間 (分鐘)</th>
                            <th scope='col' className='whitespace-nowrap px-4 py-3 text-right'>平均完成次數</th>
                            <th scope='col' className='whitespace-nowrap px-4 py-3 text-right sm:pr-6'>平均每次時間</th>
                        </tr>
                    </thead>
                    <tbody className='divide-y divide-line-soft'>
                        {loading && Array.from({ length: 4 }).map((_, i) => <tr key={i}>
                            {Array.from({ length: 6 }).map((__, j) => <td key={j} className='px-4 py-4'><Skeleton className='h-4 w-full' /></td>)}
                        </tr>)}
                        {exercises.map((e) => <tr key={e.exercise} className='transition-colors hover:bg-canvas'>
                            <td className='whitespace-nowrap px-4 py-3 font-medium text-ink sm:px-6'>{getExerciseName(e.exercise)}</td>
                            <td className='px-4 py-3 text-right tabular-nums'>{formatInt(e.sessions)}</td>
                            <td className='px-4 py-3 text-right tabular-nums'>{formatInt(e.reps)}</td>
                            <td className='px-4 py-3 text-right tabular-nums'>{formatInt(e.seconds / 60)}</td>
                            <td className='px-4 py-3 text-right tabular-nums'>{formatFixed(e.reps / e.sessions, 1)}</td>
                            <td className='whitespace-nowrap px-4 py-3 text-right tabular-nums sm:pr-6'>{formatMinSec(e.seconds / e.sessions)}</td>
                        </tr>)}
                    </tbody>
                </table>
            </div>
        </Section>
    </div>
}
